
import { useEffect, useState, useRef } from 'react';

interface CounterProps {
  end: number;
  duration?: number;
  suffix?: string;
  prefix?: string;
}

function AnimatedCounter({ end, duration = 2000, suffix = '', prefix = '' }: CounterProps) {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [hasStarted, end, duration]);

  return (
    <span ref={ref}>
      {prefix}{count.toLocaleString('pt-BR')}{suffix}
    </span>
  );
}

export default function StatsCounter() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const stats = [
    {
      value: 12500,
      suffix: '+',
      label: 'Clientes Atendidos',
      icon: 'ri-user-star-line',
      color: 'from-primary to-cyan-400',
    },
    {
      value: 48000,
      suffix: '+',
      label: 'Horas Economizadas',
      icon: 'ri-time-line',
      color: 'from-emerald-400 to-teal-500',
    },
    {
      value: 2500000,
      suffix: '+',
      label: 'Mensagens Respondidas',
      icon: 'ri-message-3-line',
      color: 'from-violet-400 to-purple-500',
    },
    {
      value: 98,
      suffix: '%',
      label: 'Taxa de Satisfação',
      icon: 'ri-heart-pulse-line',
      color: 'from-rose-400 to-pink-500',
    },
  ];

  return (
    <section ref={sectionRef} className="relative py-12 sm:py-20 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900/50 to-black"></div>
      
      {/* Animated grid pattern */}
      <div className="absolute inset-0 opacity-10">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 102, 255, 0.3) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(0, 102, 255, 0.3) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        ></div>
      </div>

      {/* Glowing orbs */}
      <div className="absolute top-1/2 left-1/4 w-32 sm:w-64 h-32 sm:h-64 bg-primary/20 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 right-1/4 w-32 sm:w-64 h-32 sm:h-64 bg-cyan-500/20 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section header */}
        <div 
          className={`text-center mb-10 sm:mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/20 text-primary text-xs sm:text-sm font-semibold rounded-full mb-3 sm:mb-4 whitespace-nowrap">
            Resultados Comprovados
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
            Números que <span className="text-primary">falam por si</span>
          </h2>
          <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto px-2">
            Milhares de empreendedores já transformaram seu atendimento com a NeuroAgents
          </p>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 md:gap-8">
          {stats.map((stat, index) => (
            <div
              key={stat.label}
              className={`relative group transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Card */}
              <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 text-center overflow-hidden hover:border-primary/50 transition-all duration-300 hover:scale-105">
                {/* Gradient glow on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                
                {/* Icon */}
                <div className={`w-10 h-10 sm:w-14 sm:h-14 mx-auto mb-3 sm:mb-4 rounded-lg sm:rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
                  <i className={`${stat.icon} text-lg sm:text-2xl text-white`}></i>
                </div>

                {/* Counter */}
                <div className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-1 sm:mb-2">
                  <AnimatedCounter 
                    end={stat.value} 
                    suffix={stat.suffix}
                    duration={2500}
                  />
                </div>

                {/* Label */}
                <p className="text-gray-400 text-[10px] sm:text-sm md:text-base font-medium">
                  {stat.label}
                </p>

                {/* Decorative corner */}
                <div className={`absolute -bottom-2 -right-2 w-12 sm:w-16 h-12 sm:h-16 bg-gradient-to-br ${stat.color} opacity-20 rounded-full blur-xl`}></div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom highlight */}
        <div 
          className={`mt-8 sm:mt-12 text-center transition-all duration-700 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-gray-500 text-xs sm:text-sm flex items-center justify-center gap-2">
            <i className="ri-shield-check-line text-primary"></i>
            <span>Dados atualizados em tempo real baseados em nossa plataforma</span>
          </p>
        </div>
      </div>
    </section>
  );
}
